import { useCallback } from "react";
import { useAppDispatch, useAppSelector } from "@/app/store/hooks";
import { ModalFeedbackForm } from "@/features/feedbacks/ui/ModalFeedbackForm";
import { cn } from "@/shared/lib/utils";
import Typography from "@/shared/ui/Typography";
import {
    selectModalPresentationLink,
    selectRequestModalFormData,
} from "../model/selects";

import CloseIcon from "@/assets/icons/app/close.svg?react";
import { complatedModalOpen, requestFormModalClose } from "../model/slice";

export interface IModalClientRequestProps {
    className?: string;
}

export function ModalClientRequest({ className }: IModalClientRequestProps) {
    const dispatch = useAppDispatch();

    const modalFormData = useAppSelector(selectRequestModalFormData);
    const presentationLink = useAppSelector(selectModalPresentationLink);

    const handleSubmit = useCallback(
        (name: string, phone: string) => {
            if (process.env.NODE_ENV === "development") {
                console.log(name, phone);
            }

            if (presentationLink && modalFormData.download) {
                const link = document.createElement("a");
                link.href = presentationLink;
                link.download = "";
                link.target = "_blank";
                link.rel = "noopener noreferrer";

                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }

            dispatch(requestFormModalClose());
            dispatch(complatedModalOpen());
        },
        [dispatch, presentationLink, modalFormData]
    );

    const handleModalClose = useCallback(() => {
        dispatch(requestFormModalClose());
    }, [dispatch]);

    if (!modalFormData) return null;

    return (
        <div
            className={cn(
                "fixed z-30 w-screen h-screen overflow-x-hidden overflow-y-auto scrollbar-hide left-0 top-0 bg-light-blue-50 shadow-glass flex justify-center items-center",
                className
            )}
        >
            <div className="relative w-[355px] md:w-[704px] lg:w-[1040px] xl:w-[1240px]">
                <button
                    type="button"
                    aria-label="Закрыть модальное окно"
                    onClick={handleModalClose}
                    className="absolute z-10 top-[10px] right-[10px] md:top-[20px] md:right-[20px] lg:top-[30px] lg:right-[30px] xl:top-[40px] xl:right-[40px]
                        w-[36px] h-[36px] md:w-[40px] md:h-[40px] flex justify-center items-center bg-white-50 shadow-glass rounded-full"
                >
                    <CloseIcon className="w-[16px] h-[16px] md:w-[20px] md:h-[20px]" />
                </button>

                <div className="py-[24px] px-[10px] md:p-[40px] bg-white-50 shadow-glass rounded-[10px] flex flex-row items-center gap-[20px]">
                    <div className="w-full lg:w-[470px] xl:w-[570px] flex flex-col gap-[12px] md:gap-[32px]">
                        {modalFormData.title && (
                            <Typography tag="h2">
                                {modalFormData.title}
                            </Typography>
                        )}

                        {modalFormData.text && (
                            <Typography className="uppercase font-medium text-[10px] md:text-[14px] lg:text-[18px] lg:font-semibold !leading-[120%]">
                                {modalFormData.text}
                            </Typography>
                        )}

                        <ModalFeedbackForm
                            download={modalFormData.download}
                            onSubmit={handleSubmit}
                        />
                    </div>

                    <div className="hidden lg:flex w-[470px] xl:w-[570px]">
                        <img
                            className="w-full h-auto object-contain"
                            src="/images/defaults/modal_form.png"
                            alt=""
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}
